import React, { useState, useEffect } from "react";
import { fetchData } from "../../../../utils/functions";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import NoData, { hasData } from "../../../common/NoData";
import DashboardBarChart from "../../../common/DashboardBarChart";
import { getCategoryColors } from "../../../../utils/chartTheme";
import { formatCountFit } from "../../../../utils/numberFormat";

const hasSeries = (chartData) =>
  hasData((chartData?.data || []).map((item) => item.count));

const OthersTab = ({ filters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState({
    transportUsage: { categories: [], series: [] },
    incomeStatistics: { mean: 0, min: 0, max: 0, median: 0, std: 0 },
    incomeBrackets: { data: [], total: 0 },
    incomeSources: { data: [], total: 0 },
    socialTransfersAwareness: { data: [], total: 0 },
    socialTransfersReceipt: { data: [], total: 0 },
    mealsPerDay: { data: [], total: 0 },
    expenditureStatistics: { mean: 0, min: 0, max: 0 },
    expenditureBreakdown: { data: [], total: 0 },
    debtStatus: { data: [], total: 0 },
    loanSources: { data: [], total: 0 },
    debtRepaidStatus: { data: [], total: 0 },
  });

  const [loading, setLoading] = useState(true);

  const formatNumber = (num) => {
    if (num == null || typeof num !== "number" || isNaN(num)) return "0";
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [
          transportRes,
          incomeStatsRes,
          incomeBracketsRes,
          incomeSourcesRes,
          socialAwarenessRes,
          socialReceiptRes,
          mealsRes,
          expenditureStatsRes,
          expenditureBreakdownRes,
          debtStatusRes,
          loanSourcesRes,
          debtRepaidRes,
        ] = await Promise.all([
          fetchData("transport-usage-by-purpose", "GET", null, {}, filters, navigate, "/transport", dispatch),
          fetchData("income-statistics", "GET", null, {}, filters, navigate, "/income", dispatch),
          fetchData("income-brackets", "GET", null, {}, filters, navigate, "/income", dispatch),
          fetchData("income-sources", "GET", null, {}, filters, navigate, "/income", dispatch),
          fetchData("social-transfers-awareness", "GET", null, {}, filters, navigate, "/social", dispatch),
          fetchData("social-transfers-receipt", "GET", null, {}, filters, navigate, "/social", dispatch),
          fetchData("meals-per-day", "GET", null, {}, filters, navigate, "/expenditure", dispatch),
          fetchData("expenditure-statistics", "GET", null, {}, filters, navigate, "/expenditure", dispatch),
          fetchData("expenditure-breakdown", "GET", null, {}, filters, navigate, "/expenditure", dispatch),
          fetchData("debt-status", "GET", null, {}, filters, navigate, "/debt", dispatch),
          fetchData("loan-sources", "GET", null, {}, filters, navigate, "/debt", dispatch),
          fetchData("debt-repaid-status", "GET", null, {}, filters, navigate, "/debt", dispatch),
        ]);

        setData({
          transportUsage: transportRes?.data || { categories: [], series: [] },
          incomeStatistics: incomeStatsRes?.data || { mean: 0, min: 0, max: 0, median: 0, std: 0 },
          incomeBrackets: incomeBracketsRes || { data: [], total: 0 },
          incomeSources: incomeSourcesRes || { data: [], total: 0 },
          socialTransfersAwareness: socialAwarenessRes || { data: [], total: 0 },
          socialTransfersReceipt: socialReceiptRes || { data: [], total: 0 },
          mealsPerDay: mealsRes || { data: [], total: 0 },
          expenditureStatistics: expenditureStatsRes?.data || { mean: 0, min: 0, max: 0 },
          expenditureBreakdown: expenditureBreakdownRes || { data: [], total: 0 },
          debtStatus: debtStatusRes || { data: [], total: 0 },
          loanSources: loanSourcesRes || { data: [], total: 0 },
          debtRepaidStatus: debtRepaidRes || { data: [], total: 0 },
        });
      } catch (err) {
        console.error("Error loading Others tab:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filters, navigate, dispatch]);

  const createPieChartOptions = (labels) => ({
    chart: { type: "pie", height: 280 },
    plotOptions: { pie: { dataLabels: { minAngleToShowLabel: 18 } } },
    labels,
    colors: getCategoryColors(labels),
    dataLabels: {
      enabled: true,
      style: { fontSize: "11px", fontWeight: "bold", colors: ["#fff"] },
      formatter: (val) => `${val.toFixed(1)}%`,
      dropShadow: { enabled: false },
    },
    legend: { show: true, position: "bottom", fontSize: "10px", horizontalAlign: "center", offsetY: 0 },
    tooltip: { enabled: false },
  });

  const createDonutChartOptions = (labels, holeSize = "65%") => ({
    chart: { type: "donut", height: 280 },
    labels,
    colors: getCategoryColors(labels),
    dataLabels: {
      enabled: true,
      style: { fontSize: "11px", fontWeight: "bold", colors: ["#fff"] },
      formatter: (val) => `${val.toFixed(1)}%`,
      dropShadow: { enabled: false },
    },
    plotOptions: {
      pie: {
        dataLabels: { minAngleToShowLabel: 18 },
        donut: {
          size: holeSize,
          labels: {
            show: true,
            value: { fontSize: "18px", fontWeight: 700, formatter: (val) => formatCountFit(val) },
            total: { show: true, label: "Total", fontSize: "14px", fontWeight: 600, color: "#374151", formatter: (w) => formatCountFit(w.globals.seriesTotals.reduce((a, b) => a + b, 0)) },
          },
        },
      },
    },
    legend: { show: true, position: "bottom", fontSize: "10px", horizontalAlign: "center", offsetY: 0 },
    tooltip: { enabled: false },
  });

  const renderTransportSection = () => {
    if (!has.transport) {
      return <NoData />;
    }
    return (
      <DashboardBarChart
        vertical
        stacked={false}
        categories={data.transportUsage.categories}
        series={(data.transportUsage.series || []).filter((s) => hasData(s.data))}
        unit="households"
      />
    );
  };

  const renderBarChartSection = (chartData, chartType = "default") => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }
  
    return <DashboardBarChart items={chartData.data} vertical={chartType !== "distributed"} />;
  };

  const renderPieChartSection = (chartData) => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }
    const labels = chartData.data.map((item) => item.name);
    const values = chartData.data.map((item) => item.count);
    const chartOptions = createPieChartOptions(labels);
    return (
      <div className="h-full flex flex-col">
        <div className="flex-grow">
          <ReactApexChart options={chartOptions} series={values} type="pie" height={280} />
        </div>
      </div>
    );
  };

  const renderDonutChartSection = (chartData, holeSize) => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }
    const labels = chartData.data.map((item) => item.name);
    const values = chartData.data.map((item) => item.count);
    const chartOptions = createDonutChartOptions(labels, holeSize);
    return (
      <div className="h-full flex flex-col">
        <div className="flex-grow">
          <ReactApexChart options={chartOptions} series={values} type="donut" height={280} />
        </div>
      </div>
    );
  };

  const renderModernStatisticsCards = (stats, title, isExpenditure = false) => {
    return (
      <div className="bg-white flex flex-col h-full">
        <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase mb-4 text-balance">
          {title}
        </h3>
        {!hasData(stats) ? <NoData /> : (
        <div className="flex-1 flex flex-col justify-center space-y-4">
          {/* Mean */}
          <div className="relative rounded-xl bg-[#137E81] p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-white/90">Mean</div>
                <div className="text-3xl font-bold text-white">
                  {formatNumber(stats.mean)}
                </div>
              </div>
            </div>
          </div>

          {/* For Income: show Median & Std Dev */}
          {!isExpenditure && (
            <div className="grid grid-cols-2 gap-4">
              <div className="relative rounded-xl bg-[#E6F2F2] border border-[#7FBFC1] p-4">
                <div className="text-xs font-semibold text-[#0B5557]">Median</div>
                <div className="text-2xl font-bold text-[#137E81]">
                  {formatNumber(stats.median)}
                </div>
              </div>
              <div className="relative rounded-xl bg-[#E6F2F2] border border-[#7FBFC1] p-4">
                <div className="text-xs font-semibold text-[#0B5557]">Std Dev</div>
                <div className="text-2xl font-bold text-[#137E81]">
                  {formatNumber(stats.std)}
                </div>
              </div>
            </div>
          )}

          {/* Min & Max */}
          <div className="grid grid-cols-2 gap-4">
            <div className="relative rounded-xl bg-[#E6F2F2] border border-[#7FBFC1] p-4">
              <div className="text-xs font-semibold text-[#0B5557]">Min</div>
              <div className="text-2xl font-bold text-[#137E81]">
                {formatNumber(stats.min)}
              </div>
            </div>
            <div className="relative rounded-xl bg-[#E6F2F2] border border-[#7FBFC1] p-4">
              <div className="text-xs font-semibold text-[#0B5557]">Max</div>
              <div className="text-2xl font-bold text-[#137E81]">
                {formatNumber(stats.max)}
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    );
  };

  const has = {
    transport: hasData(
      (data.transportUsage.series || []).map((s) => s.data)
    ),
    incomeStatistics: hasData(data.incomeStatistics),
    incomeBrackets: hasSeries(data.incomeBrackets),
    incomeSources: hasSeries(data.incomeSources),
    socialTransfersAwareness: hasSeries(data.socialTransfersAwareness),
    socialTransfersReceipt: hasSeries(data.socialTransfersReceipt),
    mealsPerDay: hasSeries(data.mealsPerDay),
    expenditureStatistics: hasData(data.expenditureStatistics),
    expenditureBreakdown: hasSeries(data.expenditureBreakdown),
    debtStatus: hasSeries(data.debtStatus),
    loanSources: hasSeries(data.loanSources),
    debtRepaidStatus: hasSeries(data.debtRepaidStatus),
  };

  const anyData = Object.values(has).some(Boolean);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#137E81]"></div>
        <p className="mt-4 text-gray-600">Loading data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {!Object.values(has).some(Boolean) && (
        <NoData className="py-16" />
      )}
      {/* Transport */}
      {anyData && (
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Transport Mode Usage by Purpose
          </h3>
          <div className="flex-grow">{renderTransportSection()}</div>
        </div>
      </div>
      )}

      {/* Income Row 1 */}
      {anyData && <hr className="border-t-2 border-dashed border-teal-600 first:hidden" />}
      {anyData && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Income Statistics – full set (Mean, Median, Std Dev, Min, Max) */}
        {renderModernStatisticsCards(data.incomeStatistics, "Income Statistics")}

        {/* Total Monthly Income Distribution */}
        <div className="bg-white flex flex-col lg:col-span-2">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Total Monthly Income Distribution
          </h3>
          <div className="flex-grow">{renderBarChartSection(data.incomeBrackets, "income")}</div>
        </div>
      </div>
      )}

      {/* Income Row 2 */}
      {anyData && (
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Sources of Incomes
          </h3>
          <div className="flex-grow">{renderBarChartSection(data.incomeSources, "distributed")}</div>
        </div>
      </div>
      )}

      {/* Social Transfers */}
      {anyData && (<>
      <hr className="border-t-2 border-dashed border-teal-600 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white flex flex-col lg:col-span-2">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Awareness of Social Protection Programs
          </h3>
          <div className="flex-grow">{renderBarChartSection(data.socialTransfersAwareness, "social")}</div>
        </div>
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Receipt of Social Assistance
          </h3>
          <div className="flex-grow">{renderDonutChartSection(data.socialTransfersReceipt, "50%")}</div>
        </div>
      </div>
      </>)}

      {/* Expenditure Row */}
      {anyData && (<>
      <hr className="border-t-2 border-dashed border-teal-600 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Meals per Day */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Number of Meals per Day
          </h3>
          <div className="flex-grow">{renderPieChartSection(data.mealsPerDay)}</div>
        </div>

        {/* Household Expenditure Statistics */}
        {renderModernStatisticsCards(data.expenditureStatistics, "Household Expenditure Statistics", true)}

        {/* Expenditure Breakdown */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Monthly Expenditure Categories
          </h3>
          <div className="flex-grow">{renderDonutChartSection(data.expenditureBreakdown)}</div>
        </div>
      </div>
      </>)}

      {/* Debt */}
      {anyData && (<>
      <hr className="border-t-2 border-dashed border-teal-600 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Outstanding Loans Status
          </h3>
          <div className="flex-grow">{renderPieChartSection(data.debtStatus)}</div>
        </div>
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Sources of Household Loans
          </h3>
          <div className="flex-grow">{renderBarChartSection(data.loanSources, "debt")}</div>
        </div>
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Loan Repayment Status
          </h3>
          <div className="flex-grow">{renderDonutChartSection(data.debtRepaidStatus)}</div>
        </div>
      </div>
      </>)}
    </div>
  );
};

export default OthersTab;